// Log transport pipeline: dedupe → throttle → buffer → batch → sink.
//
// Phase 1.6: no-op default sink. Later phases plug in:
//   - Phase 1.8: NewRelic HTTP sink
//   - Phase 5.2: Sentry sink for error-level only
//
// Pipeline rules:
//   - Dedupe: same dedupe key inside DEDUPE_WINDOW_MS is dropped.
//   - Throttle: hard cap of HARD_CAP_PER_MIN records/min/session. Excess
//     dropped; a single summary record is emitted at the end of the minute.
//   - Buffer: records accumulate up to logEnv.batchSize OR logEnv.flushMs,
//     whichever comes first.
//   - Breadcrumbs: a ring of the most recent BREADCRUMB_SIZE non-error
//     records is kept in memory so error sinks (Sentry) can attach context.
//   - Flush: explicit flush() drains the buffer immediately. The module
//     also flushes on 'pagehide' / 'beforeunload' using sendBeacon-style
//     synchronous transport (hooked up in Phase 1.8).

import { logEnv } from './env';

export type Sink = (batch: LogRecord[]) => void;

// Minimal record shape recognised by the transport. The full LogRecord
// (with userId, route, etc.) is built by logger.ts in Phase 1.7; the
// transport only needs these fields for dedupe/throttle decisions.
export interface LogRecord {
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  event: string;
  route?: string;
  status?: number;
  module?: string;
  // Anything else permitted; transport is structure-agnostic beyond the above.
  [key: string]: unknown;
}

const DEDUPE_WINDOW_MS = 2000;
const HARD_CAP_PER_MIN = 200;
const BREADCRUMB_SIZE = 30;

// ---- Sink registry --------------------------------------------------------

let activeSink: Sink = noopSink;

function noopSink(_batch: LogRecord[]): void {
  // Drops silently — Phase 1.6 default.
}

export function setSink(sink: Sink): void {
  activeSink = sink;
}

export function getSink(): Sink {
  return activeSink;
}

// ---- Dedupe ---------------------------------------------------------------

const recentKeyTs = new Map<string, number>();

function dedupeKey(r: LogRecord): string {
  return `${r.level}|${r.event}|${r.route ?? ''}|${r.status ?? ''}`;
}

function isDuplicate(r: LogRecord, now: number): boolean {
  const key = dedupeKey(r);
  const last = recentKeyTs.get(key);
  if (last != null && now - last < DEDUPE_WINDOW_MS) return true;
  recentKeyTs.set(key, now);
  // Bound the map.
  if (recentKeyTs.size > 500) {
    const firstKey = recentKeyTs.keys().next().value as string | undefined;
    if (firstKey !== undefined) recentKeyTs.delete(firstKey);
  }
  return false;
}

// ---- Throttle (hard cap per minute) --------------------------------------

let minuteWindowStart = 0;
let minuteCount = 0;
let droppedThisMinute = 0;

function throttle(r: LogRecord, now: number): boolean {
  if (now - minuteWindowStart >= 60_000) {
    if (droppedThisMinute > 0) {
      // Emit a single summary record for the previous minute. We push it
      // directly to the buffer so it can't itself be throttled.
      buffer.push({
        timestamp: new Date().toISOString(),
        level: 'warn',
        event: 'log_throttle_dropped',
        module: 'observability',
        data: { droppedCount: droppedThisMinute, windowMs: 60_000 },
      } as LogRecord);
    }
    minuteWindowStart = now;
    minuteCount = 0;
    droppedThisMinute = 0;
  }
  if (minuteCount >= HARD_CAP_PER_MIN) {
    droppedThisMinute++;
    return true;
  }
  minuteCount++;
  // Errors bypass throttling? No — keep cap honest; volume cliff is the signal.
  void r;
  return false;
}

// ---- Breadcrumbs ----------------------------------------------------------

const breadcrumbRing: LogRecord[] = [];

function pushBreadcrumb(r: LogRecord): void {
  if (r.level === 'error') return; // breadcrumbs are the lead-up, not the error itself
  breadcrumbRing.push(r);
  if (breadcrumbRing.length > BREADCRUMB_SIZE) breadcrumbRing.shift();
}

export const breadcrumbs = {
  snapshot(): LogRecord[] {
    return breadcrumbRing.slice();
  },
  clear(): void {
    breadcrumbRing.length = 0;
  },
};

// ---- Buffer + batching ---------------------------------------------------

let buffer: LogRecord[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush(): void {
  if (flushTimer != null) return;
  if (logEnv.flushMs <= 0) {
    // Immediate-flush mode (typical for dev) — drain synchronously after this tick.
    queueMicrotask(flush);
    return;
  }
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, logEnv.flushMs);
}

export function flush(): void {
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (buffer.length === 0) return;
  const batch = buffer;
  buffer = [];
  try {
    activeSink(batch);
  } catch {
    // Sinks must not crash the host app. Swallow.
  }
}

// ---- Public entry point ---------------------------------------------------

export function shipBatch(record: LogRecord): void {
  const now = Date.now();
  if (isDuplicate(record, now)) return;
  if (throttle(record, now)) return;
  pushBreadcrumb(record);
  buffer.push(record);
  if (buffer.length >= logEnv.batchSize) {
    flush();
  } else {
    scheduleFlush();
  }
}

// ---- Lifecycle hooks (browser only) --------------------------------------

if (typeof window !== 'undefined') {
  const flushOnUnload = () => flush();
  // pagehide fires for back/forward cache; beforeunload for full nav/refresh.
  window.addEventListener('pagehide', flushOnUnload);
  window.addEventListener('beforeunload', flushOnUnload);
}

// ---- Test-only reset ------------------------------------------------------

export function __resetTransportForTests(): void {
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  buffer = [];
  recentKeyTs.clear();
  breadcrumbRing.length = 0;
  minuteWindowStart = 0;
  minuteCount = 0;
  droppedThisMinute = 0;
  activeSink = noopSink;
}
